import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, ImageIcon, InstagramIcon, LinkedinIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, SparklesIcon, TargetIcon, UsersIcon, VideoIcon, YoutubeIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};
function scrollToForm() {
  document.getElementById('formulario')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 rounded-xl px-8 transition-transform duration-200 ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-351" data-mp="1astccrhcbx5y" /> : undefined} data-id="element-350" data-mp="2zdihve5pr53o">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-352" data-mp="10ux7uiiws0gt">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-353" data-mp="3ehxkgunjdf8">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-354" data-mp="1bn5mhb0gbtj3">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-355" data-mp="16pdwimjdahhj">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-356" data-mp="1gfqgacjcy6mm" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-357" data-mp="nrktlk8qu7k5" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-358" data-mp="ws97mey507x1">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);
  const brandMark = <BrandLogo src={config.logoUrl} size={30} alt={landing.name} data-id="element-359" data-mp="26rtvoa7ien3r" />;
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-360" data-mp="2zf6ficmacgtd">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 sm:px-8" data-id="element-361" data-mp="2ppmub0b3oavc">
              {brandMark}
              <div className="flex items-center gap-2 sm:gap-4" data-id="element-362" data-mp="2umd7nfvissd2">
                {authLinks && <Link to="/login" className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline" data-id="element-363" data-mp="3ic7mu43bat0h">
                    Iniciar sesión
                  </Link>}
                <Button size="sm" onClick={scrollToForm} className="rounded-full px-5" data-id="element-364" data-mp="1iahjv24hnugs">
                  {cfg(block, 'ctaLabel', 'Quiero información')}
                </Button>
              </div>
            </div>
          </header>;
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-365" data-mp="2613t2wq8bksd">
            {brandMark}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-5 pb-6 pt-16 text-center sm:px-8 sm:pt-24" data-id="element-366" data-mp="1x2097qj8n1ct">
            <div className="mx-auto max-w-4xl" data-id="element-367" data-mp="lljopisttuj3">
              {cfg(block, 'showLogo', false) && <div className="mb-12 flex justify-center" data-id="element-368" data-mp="gle2ii5bt08e">{brandMark}</div>}
              {eyebrow && <span className="mb-7 inline-flex items-center rounded-full border border-border bg-surface px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground" data-id="element-369" data-mp="2sabz2oan6mus">
                  {eyebrow}
                </span>}
              <h1 className="mx-auto max-w-3xl text-[34px] font-semibold leading-[1.1] tracking-[-0.02em] text-navy sm:text-[56px]" data-id="element-370" data-mp="360fd4rzdba95">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-371" data-mp="3aypb1ach8oyg" />
              </h1>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-id="element-372" data-mp="3kxyauju0wxsq">
                  {cfg(block, 'subtitle', '')}
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-373" data-mp="1qkyom7v515ch">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-374" data-mp="2lzdr3hcfs68j">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-375" data-mp="2m172tb1wpgne">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-376" data-mp="30xfyk2ml9bv2">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-377" data-mp="30utqm1vscucb">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-378" data-mp="3eo1htsfyfnth">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-379" data-mp="3jnxcv26oc4ww" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-380" data-mp="1d0gj4x3eketq">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        return <section key={block.id} className="px-5 pb-4 pt-6 sm:px-8 sm:pb-8" data-id="element-381" data-mp="1vpdc8xn2d1ad">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} data-id="element-382" data-mp="1vrg65src7ekd" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-383" data-mp="1lxqwqp3p0g1t" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-384" data-mp="1gyml7rtaq68b">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mt-8 flex justify-center" data-id="element-385" data-mp="7ruj6nl9g20t">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-386" data-mp="7sdxzvfm6aam" />
              </div>}
          </section>;
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-387" data-mp="3l8lb8w63j9ae">
            <div className="mx-auto max-w-6xl" data-id="element-388" data-mp="3b8um7a0b1mhp">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-389" data-mp="3g6w25m2fp5oy">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-390" data-mp="1efsosp15td0p">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-391" data-mp="2tzry46wd0sk6" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-392" data-mp="3hnrzs1v7m3dj">
                        <Icon className="h-6 w-6 text-primary" data-id="element-393" data-mp="3rmqxwqicmgma" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-394" data-mp="3rkehwiw5igbv">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-395" data-mp="1x7y6z4seue0o">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-396" data-mp="tc77ifznztvx">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-397" data-mp="xcr4njk8mxi2">
              {items.map(item => <div key={item.label} data-id="element-398" data-mp="nrsvukg3dl4">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-399" data-mp="5lb74uh57qd2">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-400" data-mp="9m3j3vheg9d1">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-401" data-mp="35jktrl1b8o8o">
            <div className="mx-auto max-w-6xl" data-id="element-402" data-mp="31gxwv5ps8d4m">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-403" data-mp="2wjobc15iklm2">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-404" data-mp="2rm6187frg0ch">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-405" data-mp="2mrrytl2oksyb">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-406" data-mp="28u3fiho9htvd">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-407" data-mp="1u5jylbhy66pn">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-408" data-mp="1k9qn7vfysj9g">
                      {item.avatar ? <img src={item.avatar} alt="" className="h-11 w-11 rounded-full object-cover" data-id="element-409" data-mp="1pa4jkg87e4rn" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-410" data-mp="2382ufbnbfj2e">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-411" data-mp="2vv5rdzccflqj">
                        <span className="block truncate font-semibold text-foreground" data-id="element-412" data-mp="30ngutc6alpnu">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-413" data-mp="p5vexgl6pkz0">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-414" data-mp="z043xub0avsf">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-415" data-mp="b8728w19tk51">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-416" data-mp="bat90tgc7vm7">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-417" data-mp="1mncbyjztkiy5">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-418" data-mp="3l4w9d6pymt16">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-419" data-mp="2qeogvwkh0yw">
            <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-420" data-mp="cpvr14giuyqn">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-421" data-mp="cnj9gseoeqe4">
                {cfg(block, 'title', 'Solicita información')}
              </h2>
              <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-422" data-mp="2eiaf6l9nszfi">
                {cfg(block, 'subtitle', '')}
              </p>
            </div>
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-423" data-mp="10l7k1mbj9j3u" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-424" data-mp="mulytqylwx2s">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-425" data-mp="15izaebwj4x5x">
            <div className="mx-auto max-w-3xl" data-id="element-426" data-mp="3v9mchfx3o36z">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-427" data-mp="3v79n4hp0iqjw">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-428" data-mp="5tbl9vdtejta">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-429" data-mp="zpgepdw7k81">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-430" data-mp="3n6dwtr0qw2gj">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-431" data-mp="erniwhd3mhv8" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-432" data-mp="2uk2xsksse9lu">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-433" data-mp="1k0864wa8duu6">
            <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-434" data-mp="1orgh3wr7lo9g">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" data-id="element-435" data-mp="1tpi1qm4a9vn1" />
              <h2 className="text-2xl font-semibold tracking-tight text-navy" data-id="element-436" data-mp="1xq1ttba0292b">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-437" data-mp="27palfxhe7bli">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-8" data-id="element-438" data-mp="8dgkaunnueqf">
                <Button size="lg" onClick={() => {
                const url = cfg(block, 'url', '');
                if (url) window.open(url, '_blank', 'noopener');
              }} data-id="element-439" data-mp="8ej9my5c4948">
                  Reservar hora
                </Button>
              </div>
            </div>
          </section>;
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-440" data-mp="f6n7agnrg9g6">
            <MessageCircleIcon className="h-6 w-6" data-id="element-441" data-mp="t2hhd309t4uv" />
          </a>;
      case 'cta_final':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-442" data-mp="12ykj47r7n1ha">
            <div className="mx-auto max-w-3xl text-center" data-id="element-443" data-mp="1mk49zzxhwbje">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-444" data-mp="1mijfubf3eum2">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-445" data-mp="1cleozyaayayx">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-10 flex justify-center" data-id="element-446" data-mp="1hjg98mjgsgbz">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={scrollToForm} data-id="element-447" data-mp="12w4uycvwei86" />
              </div>
            </div>
          </section>;
      case 'footer':
        return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-448" data-mp="yu9ypmstqv19">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-449" data-mp="jykadi0625td">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-450" data-mp="ovu8dm1mfpst">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-451" data-mp="1n8galcoyrl7e">
                <p data-id="element-452" data-mp="1ihgci443yx3b">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-453" data-mp="1ig5945ekrrcq">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-454" data-mp="18ja7hdn09au8">
                      {link}
                    </span>)}
                </div>
              </div>
              <div className="flex gap-3 text-muted-foreground" data-id="element-455" data-mp="zic3gvckh3bn">
                {[InstagramIcon, YoutubeIcon, LinkedinIcon].map((Icon, index) => <span key={index} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" aria-hidden="true" data-id="element-456" data-mp="ullns94oc9qr" data-mpi={index}>
                    <Icon className="h-4 w-4" data-id="element-457" data-mp="2wgje2a2z2l2u" />
                  </span>)}
              </div>
            </div>
          </footer>;
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-458" data-mp="3b81s42c9u5jk">
            <div className="mx-auto max-w-6xl" data-id="element-459" data-mp="2dkjo14tk1rlw">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-460" data-mp="2igqju4s8lmq4">
                {cfg(block, 'title', 'Servicios')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-461" data-mp="2sghdk48z3xp5">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-3" data-id="element-462" data-mp="7op9vaiusotb">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-463" data-mp="1ysh7flgocxr9" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-464" data-mp="1l1m4sqwrrcw4">
                        <Icon className="h-5 w-5 text-primary" data-id="element-465" data-mp="1g43twuz6ynlh" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-466" data-mp="1tupc0hgtmtib">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-467" data-mp="1p1mho747zg12">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-468" data-mp="s15wymqbas8q">
            <div className="mx-auto max-w-4xl" data-id="element-469" data-mp="i8qfdqixg5od">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-470" data-mp="i7fb7plkkvwq">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-471" data-mp="1axfgvqnwad0n">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-472" data-mp="w3alj71wkyoi" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-473" data-mp="10y6ec9eyrq2o">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-474" data-mp="3gti411rlm0z5" />
                    </span>
                    <span data-id="element-475" data-mp="3lnvtyonhrfwf">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-476" data-mp="3308swvm4895r">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-477" data-mp="2xzbdurwejv8h">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-478" data-mp="3grvzppdop02s">
            <div className="mx-auto max-w-6xl" data-id="element-479" data-mp="3byuhb7e9qieg">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-480" data-mp="37y0r27dcsrie">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-481" data-mp="3ir9n1n5otq4">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-482" data-mp="1r785gboyzfwi" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-483" data-mp="206v8t319evs7">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-484" data-mp="255537bf5tscr" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-485" data-mp="3t99k1ob929wk">
            <div className="mx-auto max-w-5xl" data-id="element-486" data-mp="3k70bh3zjkqlt">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-487" data-mp="3k8bev2p2rwce">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-488" data-mp="kr3nj1uq0y53">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-489" data-mp="6xm37eu7nhe0">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-490" data-mp="zmayuychyuid" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-491" data-mp="1nk8t6y3t2y5c">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-492" data-mp="uvtiuovmd40c">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-493" data-mp="qui093b5xj1n">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-494" data-mp="14sy9b0whzlmy">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-495" data-mp="zstrn7juskv5">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-496" data-mp="2b88ng80bt6oo">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-497" data-mp="2ndfmq3g27np0">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-498" data-mp="2x7o909ehykeq" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-499" data-mp="29k6h8dv8fmbr">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-500" data-mp="1e903fellvxvm">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-501" data-mp="19crnhyzl40ne">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-502" data-mp="zdtykuy8y53v">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-503" data-mp="vdrvchcv53mh">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-504" data-mp="qjnnlvvrwrkc">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-505" data-mp="7p0cwhp2rzmw">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-506" data-mp="11ehg3f6j1r68" />
                      <span className="break-words text-sm text-foreground" data-id="element-507" data-mp="nkqa2gs08pf9">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const style = wrapperStyle(block);
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle ? <div key={block.id} style={style} data-id="element-508" data-mp="231qn1es1obos">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} style={style} className={`relative cursor-pointer outline-none transition-shadow ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-509" data-mp="3nhosj6etedj8">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-510" data-mp="k29kgapzye0q">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div className="w-full bg-background" style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`
  } as React.CSSProperties} data-id="element-511" data-mp="2gwe2ilvvftp7">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-512" data-mp="upaykbz1m25i">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}